import { ActionIcon, Badge, Box, Button, Card, Group, Menu, Modal, Stack, Text } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { Link } from 'wouter';
import { dashboard, dateRange, eventsJudged, summarizeTournament, type Round, type Tournament, type TournamentSummary } from '@flow/core';
import { useDeps, useUseCases } from '../../app/services';
import { paths, useQueryState } from '../../app/routes';
import { PageShell, Section } from '../../ui/PageShell';
import { K } from '../../ui/Kbd';
import { emptyTournamentForm, TournamentForm } from './TournamentForm';
import { useDeleteTournament } from './useDeleteTournament';

const day = (ms: number) => new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

const when = (s: TournamentSummary) => dateRange(s.tournament.startDate, s.tournament.endDate, Date.now()) ?? `last opened ${day(s.lastActivity)}`;
/** "Sep 26–27 · Public Forum (CHSSA) · 2 of 3 rounds submitted" (events from the rounds judged there). */
const line = (s: TournamentSummary, events: string[]) => [when(s), ...events, progress(s)].join(' · ');
const progress = (s: TournamentSummary) => (s.rounds === 0 ? 'no rounds yet' : `${s.submitted} of ${s.rounds} round${s.rounds === 1 ? '' : 's'} submitted`);

const RowMenu = ({ t }: { t: Tournament }) => {
  const url = useQueryState();
  const deleteTournament = useDeleteTournament();
  return (
    <Box onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <Menu position="bottom-end">
        <Menu.Target><ActionIcon aria-label={`${t.name} menu`}><IconDots size={16} /></ActionIcon></Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => url.set({ modal: 'edit-tournament', t: t.id })}>Edit name and dates</Menu.Item>
          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => void deleteTournament(t)}>Delete</Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
};

/** Today's tournament(s): big, with the next round one tap away. */
const CurrentCard = ({ s, next, events }: { s: TournamentSummary; next: Round | undefined; events: string[] }) => (
  <Card padding="md" className="hero" data-testid="current-tournament" data-tournament={s.tournament.name}>
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Box component={Link} href={paths.tournament(s.tournament.id)} style={{ minWidth: 0, color: 'inherit', textDecoration: 'none', flex: 1 }}>
        <Group gap={6}>
          <Text fw={700} size="lg" lineClamp={1}>{s.tournament.name}</Text>
          <Badge size="xs" variant="filled">now</Badge>
        </Group>
        <Text size="sm" c="dimmed">{line(s, events)}</Text>
      </Box>
      <RowMenu t={s.tournament} />
    </Group>
    <Group mt="sm" gap="xs">
      {next && (
        <Button component={Link} href={paths.round(s.tournament.id, next.id)} variant="filled" data-testid="dashboard-next-round">
          {next.label}: {next.teamA.code} v {next.teamB.code} →
        </Button>
      )}
      <Button component={Link} href={paths.tournament(s.tournament.id)} variant={next ? 'default' : 'filled'}>All rounds</Button>
      {!next && <Button component={Link} href={paths.tournament(s.tournament.id, { modal: 'new-round' })} variant="default" leftSection={<IconPlus size={14} />}>Add round</Button>}
    </Group>
  </Card>
);

const Row = ({ s, events }: { s: TournamentSummary; events: string[] }) => (
  <Group
    renderRoot={(props) => <Link href={paths.tournament(s.tournament.id)} {...props} />}
    justify="space-between"
    py={8}
    px={4}
    wrap="nowrap"
    style={{ borderBottom: '1px solid var(--grid-line)', color: 'inherit', textDecoration: 'none' }}
    data-testid="tournament-row"
    data-tournament={s.tournament.name}
  >
    <Box style={{ minWidth: 0 }}>
      <Text fw={600} lineClamp={1}>{s.tournament.name}</Text>
      <Text size="xs" c="dimmed" lineClamp={1}>{line(s, events)}</Text>
    </Box>
    <RowMenu t={s.tournament} />
  </Group>
);

/** Home after sign-in: current tournament(s) on top, then upcoming, then previous. */
export const DashboardPage = () => {
  const deps = useDeps();
  const uc = useUseCases();
  const url = useQueryState();
  const data = useLiveQuery(async () => {
    const ts = await deps.tournaments.list();
    const withRounds = await Promise.all(ts.map(async (t) => ({ t, rounds: await deps.rounds.listByTournament(t.id) })));
    const board = dashboard(withRounds.map(({ t, rounds }) => summarizeTournament(t, rounds)), Date.now());
    const nextRound = new Map(withRounds.map(({ t, rounds }) => [t.id, [...rounds]
      .sort((a, b) => (a.startTime || '99').localeCompare(b.startTime || '99') || a.createdAt - b.createdAt)
      .find((r) => r.status !== 'submitted' && r.status !== 'committed')]));
    const events = new Map(withRounds.map(({ t, rounds }) => [t.id, eventsJudged(rounds)]));
    return { board, nextRound, events, all: ts };
  }, []);
  if (!data) return null;
  const { board, nextRound, events, all } = data;

  const modal = url.get('modal');
  const editing = modal === 'edit-tournament' ? all.find((t) => t.id === url.get('t')) : undefined;
  const closeModal = () => url.close('modal', 't');
  const newButton = <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => url.set({ modal: 'new-tournament' })} data-testid="new-tournament">New tournament</Button>;

  return (
    <PageShell title="Tournaments">
      {all.length === 0 && (
        <Card withBorder maw={480} mx="auto" mt="xl" padding="lg">
          <Text fw={600} mb={4}>Which tournament are you judging?</Text>
          <Text size="sm" c="dimmed" mb="md">Add it, then add rounds as you get your pairings.</Text>
          <Button variant="filled" leftSection={<IconPlus size={14} />} onClick={() => url.set({ modal: 'new-tournament' })} data-testid="new-tournament">New tournament</Button>
        </Card>
      )}

      {board.current.length > 0 && (
        <Section title="Current" right={newButton}>
          <Stack gap="sm">{board.current.map((s) => <CurrentCard key={s.tournament.id} s={s} next={nextRound.get(s.tournament.id)} events={events.get(s.tournament.id) ?? []} />)}</Stack>
        </Section>
      )}
      {board.upcoming.length > 0 && (
        <Section title="Upcoming" right={board.current.length === 0 ? newButton : undefined}>
          <Stack gap={0}>{board.upcoming.map((s) => <Row key={s.tournament.id} s={s} events={events.get(s.tournament.id) ?? []} />)}</Stack>
        </Section>
      )}
      {board.previous.length > 0 && (
        <Section title="Previous" right={board.current.length + board.upcoming.length === 0 ? newButton : undefined}>
          <Stack gap={0}>{board.previous.map((s) => <Row key={s.tournament.id} s={s} events={events.get(s.tournament.id) ?? []} />)}</Stack>
        </Section>
      )}

      <Text size="xs" c="dimmed">Add a tournament's dates to keep it under Current while it runs, and Upcoming before. Shortcuts: <K>⌘G</K> glossary · <K>⌘/</K> cheatsheet</Text>

      <Modal opened={modal === 'new-tournament'} onClose={closeModal} title="New tournament">
        <TournamentForm
          initial={emptyTournamentForm()}
          submitLabel="Create"
          onCancel={closeModal}
          onSubmit={async (v) => {
            const r = await uc.createTournament(v);
            if (!r.ok) return r.error;
            url.navigate(paths.tournament(r.value.id), { replace: true });
            return null;
          }}
        />
      </Modal>
      <Modal opened={!!editing} onClose={closeModal} title="Edit tournament">
        {editing && (
          <TournamentForm
            initial={{ name: editing.name, startDate: editing.startDate ?? '', endDate: editing.endDate ?? '' }}
            submitLabel="Save"
            onCancel={closeModal}
            onSubmit={async (v) => {
              const r = await uc.updateTournament(editing.id, v);
              if (r.ok) closeModal();
              return r.ok ? null : r.error;
            }}
          />
        )}
      </Modal>
    </PageShell>
  );
};
